import { cleanFloat, roundTo } from "@/lib/tool-kit/grades/rounding";

/**
 * Italian exam marks (/30), the degree mark (/110) and US letter grades.
 *
 * Sources (retrieved October 2026):
 * - The scale: exam marks run 18 (pass) to 30, with 30 e lode as the top distinction; the
 *   degree mark runs 66 (pass) to 110, with 110 e lode. University of California Education
 *   Abroad, University of Bologna academics page: "grades are assigned on a scale from 10 to
 *   30. An 18 is a pass."
 * - The degree mark: Università di Bologna, Laurea in Ingegneria elettronica, "Redazione della
 *   tesi e voto finale" (https://corsi.unibo.it/laurea/IngegneriaElettronica/redazione-della-tesi-e-voto-finale):
 *   the weighted average of the exam marks (each mark × its credits ÷ total credits; exams
 *   marked only "idoneo" are excluded and a 30 e lode counts as 30) is converted to 110 by
 *   × 110 ÷ 30; lodi do not enter the average; the commission awards the final-exam points
 *   (at most 4 on that programme, with a speed factor of 1.0325 for finishing on time, which is
 *   not modelled here); the exam is passed with at least 66 and at most 110; 110 e lode needs a
 *   unanimous commission. Other universities and programmes use different bonus points and
 *   rounding, so the bonus here is an input.
 * - Two published US tables for Italian exam marks:
 *   "montana": University of Montana Global Engagement, "Italy Credit Transfer Information"
 *   (2020): 27–30 A, 21–26 B, 18–20 C, 17 or below F.
 *   "uw": University of Washington Graduate School, "Computing Grade Point Averages — All
 *   Countries" (updated 2019): 27–30 A, 24–26 B, 21–23 C, 18–20 D, 0–17 F.
 *   Both print letters only, so the grade points are the plain US values A 4, B 3, C 2, D 1, F 0.
 *   The two tables disagree for marks of 18 to 23, which is why the scale is a choice, and a
 *   custom table is offered.
 *
 * Marks are whole numbers; a mark below 18 is a fail, so it is not accepted as a result here.
 */

export type ItalianScale = "montana" | "uw" | "custom";

export const ITALIAN_SCALES: readonly ItalianScale[] = [
  "montana",
  "uw",
  "custom",
];

export const ITALIAN_PASS_MARK = 18;
export const ITALIAN_MAX_MARK = 30;
export const DEGREE_PASS_MARK = 66;
export const DEGREE_MAX_MARK = 110;

export interface ItalianBand {
  /** Lowest mark, inclusive. */
  min: number;
  letter: string;
  points: number;
}

const MONTANA: readonly ItalianBand[] = [
  { min: 27, letter: "A", points: 4 },
  { min: 21, letter: "B", points: 3 },
  { min: 18, letter: "C", points: 2 },
  { min: 0, letter: "F", points: 0 },
];

const UW: readonly ItalianBand[] = [
  { min: 27, letter: "A", points: 4 },
  { min: 24, letter: "B", points: 3 },
  { min: 21, letter: "C", points: 2 },
  { min: 18, letter: "D", points: 1 },
  { min: 0, letter: "F", points: 0 },
];

export interface CustomMins {
  a: number;
  b: number;
  c: number;
  d: number;
}

export const DEFAULT_CUSTOM_MINS: CustomMins = { a: 27, b: 24, c: 21, d: 18 };

/** Cutoffs must fall in 18–30 and descend A ≥ B ≥ C ≥ D. */
export function validCustomMins(mins: CustomMins): boolean {
  const values = [mins.a, mins.b, mins.c, mins.d];
  return (
    values.every(
      (value) =>
        Number.isInteger(value) &&
        value >= ITALIAN_PASS_MARK &&
        value <= ITALIAN_MAX_MARK
    ) &&
    mins.a >= mins.b &&
    mins.b >= mins.c &&
    mins.c >= mins.d
  );
}

export function italianBands(
  scale: ItalianScale,
  custom: CustomMins = DEFAULT_CUSTOM_MINS
): readonly ItalianBand[] {
  if (scale === "montana") {
    return MONTANA;
  }
  if (scale === "uw") {
    return UW;
  }
  return [
    { min: custom.a, letter: "A", points: 4 },
    { min: custom.b, letter: "B", points: 3 },
    { min: custom.c, letter: "C", points: 2 },
    { min: custom.d, letter: "D", points: 1 },
    { min: 0, letter: "F", points: 0 },
  ];
}

export function isItalianExamMark(mark: number): boolean {
  return (
    Number.isInteger(mark) &&
    mark >= ITALIAN_PASS_MARK &&
    mark <= ITALIAN_MAX_MARK
  );
}

export function convertItalianMark(
  mark: number,
  bands: readonly ItalianBand[]
): ItalianBand | null {
  if (!isItalianExamMark(mark)) {
    return null;
  }
  return bands.find((band) => mark >= band.min) ?? null;
}

export interface ItalianExam {
  mark: number;
  /** CFU/ECTS credits, the weight of the exam. */
  credits: number;
  /** "30 e lode"; only counts for a mark of 30. */
  lode: boolean;
}

export interface ItalianSummary {
  /** Credit-weighted average of the marks out of 30 (lode counts as 30). */
  average: number;
  gpa: number;
  /** average ÷ 30, as a fraction (26 → 0.8667). */
  share: number;
  /** average × 110 ÷ 30, before any commission points. */
  baseDegreeMark: number;
  lodi: number;
  totalCredits: number;
  examCount: number;
}

export function summariseItalian(
  exams: readonly ItalianExam[],
  bands: readonly ItalianBand[]
): ItalianSummary | null {
  let credits = 0;
  let markSum = 0;
  let pointSum = 0;
  let lodi = 0;
  let examCount = 0;
  for (const exam of exams) {
    const band = convertItalianMark(exam.mark, bands);
    if (band && exam.credits > 0) {
      credits += exam.credits;
      markSum += exam.mark * exam.credits;
      pointSum += band.points * exam.credits;
      examCount += 1;
      if (exam.lode && exam.mark === ITALIAN_MAX_MARK) {
        lodi += 1;
      }
    }
  }
  if (credits <= 0) {
    return null;
  }
  const average = cleanFloat(markSum / credits);
  return {
    average,
    gpa: cleanFloat(pointSum / credits),
    share: cleanFloat(average / ITALIAN_MAX_MARK),
    baseDegreeMark: cleanFloat((average * DEGREE_MAX_MARK) / ITALIAN_MAX_MARK),
    lodi,
    totalCredits: credits,
    examCount,
  };
}

export interface DegreeMark {
  /** Base mark plus the commission's points, unrounded. */
  raw: number;
  /** Rounded to the nearest whole mark and capped at 110. */
  final: number;
  /** True when the rounded total exceeded 110 and was capped. */
  capped: boolean;
  passed: boolean;
  /** "110 e lode": the final mark is 110 and the commission grants the lode. */
  lode: boolean;
}

export function degreeMark(
  baseDegreeMark: number,
  bonusPoints: number,
  lodeGranted: boolean
): DegreeMark {
  const raw = cleanFloat(baseDegreeMark + bonusPoints);
  const rounded = roundTo(raw, 0);
  const final = Math.min(rounded, DEGREE_MAX_MARK);
  return {
    raw,
    final,
    capped: rounded > DEGREE_MAX_MARK,
    passed: final >= DEGREE_PASS_MARK,
    lode: lodeGranted && final === DEGREE_MAX_MARK,
  };
}

/** A degree mark expressed on the 30-point exam scale (66 → 18, 110 → 30). */
export function degreeToExamScale(mark: number): number {
  return cleanFloat((mark * ITALIAN_MAX_MARK) / DEGREE_MAX_MARK);
}
